import React, { useMemo, useState, useEffect } from "react";
import dayjs from "dayjs";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  postRaiseRequest,
  postRaiseRequestApi,
  postAttendanceCorrection,
  getAllServices,
  getClaimsTypes,
  getExpensiveTypes,
  getReimbursementTypes,
} from "../../ApiHelper/url_Helpers";
import { toasterSuccess } from "../../Components/toaster";
import AttachmentPreview from "../../Components/AttachmentPreview";
import Loader from "../../Components/Loader";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "application/pdf"];

const RaiseRequest = ({ isOpen, onClose, list, oncLickAction }) => {
  const [loading, setLoading] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [claimOptions, setClaimOptions] = useState([]);
  const [expenseOptions, setExpenseOptions] = useState([]);
  const [reimbursementOptions, setReimbursementOptions] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const fetchOptions = async () => {
        try {
          const [services, claims, expenses, reimbursements] = await Promise.all([
            getAllServices(),
            getClaimsTypes(),
            getExpensiveTypes(),
            getReimbursementTypes(),
          ]);
          const serviceList = (services?.results || [])
            .map((s) => s.label)
            // .filter((l) => l !== "Leave" && l !== "Attendance Correction");
            .filter((l) => l !== "Leave");

          setServiceOptions(serviceList);
          setClaimOptions(claims?.results || claims || []);
          setExpenseOptions(expenses?.results || expenses || []);
          setReimbursementOptions(reimbursements?.results || reimbursements || []);

          if (serviceList.length > 0 && !serviceList.includes(serviceType)) {
            setServiceType(serviceList[0]);
          }
        } catch (error) {
          console.error("Error fetching options:", error);
        }
      };
      fetchOptions();
    }
  }, [isOpen]);

  const fieldConfig = useMemo(
    () => ({
      Claim: [
        {
          name: "claim_type",
          label: "Claim Type",
          type: "select",
          required: true,
          options: claimOptions,
        },
        {
          name: "expense_date",
          label: "Date of Expense",
          type: "date",
          required: true,
          initialValue: "2026-08-25",
        },
        {
          name: "amount",
          label: "Amount",
          type: "number",
          required: true,
          min: 1,
        },
        {
          name: "supporting_documents",
          label: "Supporting Documents",
          type: "file",
          multiple: true,
        },
        {
          name: "claim_details",
          label: "Claim Details",
          type: "textarea",
          required: false,
        },
      ],

      Expense: [
        {
          name: "expense_category",
          label: "Expense Category",
          type: "select",
          required: true,
          options: expenseOptions,
        },
        {
          name: "amount",
          label: "Amount",
          type: "number",
          required: true,
          min: 1,
        },
        {
          name: "expense_date",
          label: "Date of Expense",
          type: "date",
          required: true,
        },
        {
          name: "attachment",
          label: "Upload Bills",
          type: "file",
          multiple: true,
        },
      ],

      "Salary Advance": [
        {
          name: "advance_amount",
          label: "Advance Amount",
          type: "number",
          required: true,
          min: 1,
        },
        {
          name: "repayment_period",
          label: "Repayment Period (months)",
          type: "select",
          required: true,
          options: [
            { id: 1, name: "1 month" },
            { id: 2, name: "2 months" },
            { id: 3, name: "3 months" },
          ],
        },
        {
          name: "reason_for_advance",
          label: "Reason For Advance",
          type: "textarea",
        },
      ],

      "Salary Hike": [
        {
          name: "current_salary",
          label: "Current Salary",
          type: "number",
          required: true,
          min: 1,
        },
        {
          name: "expected_salary",
          label: "Expected Salary",
          type: "number",
          required: true,
          min: 1,
        },
        {
          name: "reason",
          label: "Description",
          type: "textarea",
        },
      ],

      Reimbursement: [
        {
          name: "reimbursement_type",
          label: "Reimbursement Type",
          type: "select",
          required: true,
          options: expenseOptions,
        },
        {
          name: "amount",
          label: "Amount",
          type: "number",
          required: true,
          min: 1,
        },
        {
          name: "upload_receipt",
          label: "Upload Receipt",
          type: "file",
          multiple: true,
        },
        {
          name: "description",
          label: "Description",
          type: "textarea",
        },
      ],
      Resignation: [
        {
          name: "last_working_day",
          label: "Last Working Day",
          type: "date",
          required: true,
        },
        {
          name: "reason_for_leaving",
          label: "Reason for Leaving",
          type: "select",
          required: true,
          options: [
            { id: "Personal", name: "Personal" },
            { id: "Better Opportunity", name: "Better Opportunity" },
            { id: "Other", name: "Other" },
          ],
        },
        {
          name: "issue_description",
          label: "Description",
          type: "textarea",
        },
      ],
      "Attendance Correction": [
        {
          name: "attendance_date",
          label: "Attendance Date",
          type: "date",
          required: true,
        },
        {
          name: "requested_check_in",
          label: "Requested Check-In (optional)",
          type: "time",
          required: false,
        },
        {
          name: "requested_check_out",
          label: "Requested Check-Out (optional)",
          type: "time",
          required: false,
        },
        {
          name: "reason",
          label: "Reason",
          type: "textarea",
          required: true,
        },
      ]
    }),
    [claimOptions, expenseOptions,]
  );

  const buildInitialValues = (serviceType) => {
    const init = { serviceType };

    (fieldConfig[serviceType] || []).forEach((f) => {
      init[f.name] = f.type === "file" ? null : f.initialValue ?? "";
    });

    return init;
  };

  const buildSchema = (serviceType) => {
    const shape = {};

    (fieldConfig[serviceType] || []).forEach((f) => {
      if (f.type === "file") {
        let schema = Yup.mixed()
          .nullable()
          .notRequired()
          .test("fileSize", "Max 5MB allowed", (file) => {
            if (!file) return true;
            if (Array.isArray(file))
              return file.every((f) => f.size <= MAX_FILE_SIZE);
            return file.size <= MAX_FILE_SIZE;
          })
          .test("fileType", "Only PDF, PNG, JPG allowed", (file) => {
            if (!file) return true;
            if (Array.isArray(file))
              return file.every((f) => ALLOWED_FILE_TYPES.includes(f.type));
            return ALLOWED_FILE_TYPES.includes(file.type);
          });

        shape[f.name] = schema;
      } else if (f.type === "number") {
        let schema = Yup.number().typeError("Must be a number");
        if (f.required) schema = schema.required(`${f.label} is required`);
        if (f.min) schema = schema.min(f.min, `Minimum ${f.min}`);
        shape[f.name] = schema;
      } else if (f.type === "date") {
        let schema = Yup.string().test(
          "validDate",
          "Invalid date",
          (val) =>
            !val ||
            (dayjs(val).isValid() && dayjs(val).format("YYYY-MM-DD") === val)
        );
        if (f.required) schema = schema.required(`${f.label} is required`);
        shape[f.name] = schema;
      } else {
        let schema = Yup.string().trim();
        if (f.required) schema = schema.required(`${f.label} is required`);
        shape[f.name] = schema;
      }
    });

    const schema = Yup.object().shape(shape);

    if (serviceType === "Attendance Correction") {
      return schema.test(
        "at-least-one-attendance-time",
        "Enter a check-in time, check-out time, or both",
        function (values) {
          if (values?.requested_check_in || values?.requested_check_out) {
            return true;
          }

          return this.createError({
            path: "requested_check_in",
            message: "Enter a check-in time, check-out time, or both",
          });
        }
      );
    }

    return schema;
  };

  const [serviceType, setServiceType] = useState("Claim");

  const handleSubmit = async ({
    values,
    resetForm,
    onClose,
    setSubmitting,
  }) => {
    setLoading(true);

    const { serviceType, ...rest } = values;

    const payload = {
      ...rest,
      service_type: serviceType,
    };

    const noFileServices = ["Salary Hike", "Salary Advance", "Resignation"];

    try {
      if (serviceType === "Attendance Correction") {
        await postAttendanceCorrection({
          attendance_date: rest.attendance_date,
          ...(rest.requested_check_in && {
            requested_check_in: rest.requested_check_in,
          }),
          ...(rest.requested_check_out && {
            requested_check_out: rest.requested_check_out,
          }),
          reason: rest.reason,
        });
      } else if (noFileServices?.includes(serviceType)) {
        await postRaiseRequestApi(payload);
      } else {
        const form = new FormData();

        Object?.entries(payload)?.forEach(([k, v]) => {
          if (v instanceof File) {
            form.append(k, v);
          } else if (Array.isArray(v)) {
            v?.forEach((file) => form?.append(k, file));
          } else if (v !== null && v !== undefined && v !== "") {
            form.append(k, v);
          }
        });

        await postRaiseRequest(form);
      }

      toasterSuccess("Request submitted successfully");
      resetForm();
      oncLickAction();
      onClose?.();
    } catch (err) {
      console.error("Raise Request Error ", err);
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-[600px] max-h-[90vh] overflow-y-auto">
        <div className="sticky  top-0 z-40 bg-white flex justify-between items-center px-6 py-4 border-b">
          <h2 className="font-bold">Raise Request</h2>
          <button onClick={onClose}>✕</button>
        </div>

        <Formik
          enableReinitialize
          initialValues={buildInitialValues(serviceType)}
          validationSchema={buildSchema(serviceType)}
          onSubmit={(values, { setSubmitting, resetForm }) =>
            handleSubmit({
              values,
              resetForm,
              setSubmitting,
              onClose,
            })
          }
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form className="px-6 py-4 space-y-4">
              {/* Service Type */}
              <div>
                <label className="font-semibold text-sm">Service Type</label>
                <select
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  className="mt-1 w-full border rounded-lg p-2"
                >
                  <option value="">Select Service</option>
                  {serviceOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              {/* Dynamic Fields */}
              {(fieldConfig[serviceType] || []).map((f) => {
                if (
                  serviceType === "Attendance Correction" &&
                  f.name === "requested_check_out"
                ) {
                  return null;
                }

                if (
                  serviceType === "Attendance Correction" &&
                  f.name === "requested_check_in"
                ) {
                  const timeFields = [
                    f,
                    fieldConfig[serviceType].find(
                      (field) => field.name === "requested_check_out"
                    ),
                  ];

                  return (
                    <div key="attendance-times">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {timeFields.map((timeField) => (
                          <div key={timeField.name}>
                            <label className="text-sm font-semibold">
                              {timeField.label}
                            </label>
                            <Field
                              type="time"
                              name={timeField.name}
                              className="w-full border p-2 rounded"
                            />
                          </div>
                        ))}
                      </div>
                      <ErrorMessage
                        name="requested_check_in"
                        component="p"
                        className="text-xs text-red-500 mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Provide a check-in time, check-out time, or both.
                      </p>
                    </div>
                  );
                }

                return (
                  <div key={f.name}>
                  <label className="text-sm font-semibold">
                    {f.label}
                  </label>

                  {f.type === "textarea" && (
                    <Field
                      as="textarea"
                      name={f.name}
                      className="w-full border p-2 rounded"
                    />
                  )}

                  {["text", "date", "time", "number"].includes(f.type) && (
                    <Field
                      type={f.type}
                      name={f.name}
                      className="w-full border p-2 rounded"
                    />
                  )}

                  {f.type === "select" && (
                    <Field
                      as="select"
                      name={f.name}
                      className="w-full border p-2 rounded"
                    >
                      <option value="">Select</option>
                      {Array.isArray(f?.options) &&
                        f?.options?.map((o) => (
                          <option key={o?.id} value={o?.id}>
                            {o?.name}
                          </option>
                        ))}
                    </Field>
                  )}

                  {f.type === "file" && (
                    <>
                      <input
                        type="file"
                        multiple={f.multiple}
                        className="w-full border rounded-lg p-2 text-sm"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          setFieldValue(
                            f.name,
                            f.multiple ? files : files[0] || null
                          );
                        }}
                      />

                      {values[f.name] && (
                        <div className="mt-2">
                          {Array.isArray(values[f.name]) ? (
                            values[f.name].map((file, i) => (
                              <AttachmentPreview
                                key={i}
                                file={file}
                                onRemove={() => {
                                  const updated =
                                    values[f.name].filter((_, idx) => idx !== i);
                                  setFieldValue(f.name, updated);
                                }}
                                isRemove={true}
                              />
                            ))
                          ) : (
                            <AttachmentPreview
                              file={values[f.name]}
                              onRemove={() => setFieldValue(f.name, null)}
                              isRemove={true}
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <ErrorMessage
                    name={f.name}
                    component="p"
                    className="text-xs text-red-500 mt-1"
                  />
                </div>
                );
              })}

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="border px-4 py-2 rounded w-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#00B8D9] text-white px-4 py-2 rounded w-50"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </Form>
          )}
        </Formik>

        {loading && <Loader />}
      </div>
    </div>
  );
};

export default RaiseRequest;